"use client";

import { ThemeProvider } from "next-themes";
import React from "react";
import Navigation from "./navigation";
import MobileBottomNav from "./mobile-bottom-nav";
import { usePathname } from "next/navigation";
import { SidebarProvider } from "../ui/sidebar";
import { PlayerProvider } from "@/contexts/player-context";
import { GlobalPlayerControls } from "@/components/player/GlobalPlayerControls";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Skiper45 } from "../skiper45";

function FrozenRouter(props: { children: React.ReactNode }) {
  // 简化实现，直接返回子组件，让Next.js处理路由
  return <>{props.children}</>;
}

export default function MainContent({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const pathname = usePathname();
  const isMobileDevice = useIsMobile();

  // Check if current page should be full-screen (without dashboard layout)
  const isFullScreenPage = pathname === '/login' || pathname === '/signup';

  // Check if we should show global player controls (hide on settings page)
  const shouldShowPlayerControls = !pathname.startsWith('/settings');

  const layoutWrapperClass = isMobileDevice
    ? "flex flex-1 min-w-0"
    : "flex flex-1 md:p-4 lg:ps-0 min-w-0";

  const contentPaddingClass = isMobileDevice ? "px-0" : "px-0 sm:px-6";
  // 渲染内容的函数，避免重复代码
  const renderContent = () => {
    if (isFullScreenPage) {
      return (
        <div className="h-screen w-screen">
          {children}
        </div>
      );
    }

    // Mobile layout with bottom navigation
    if (isMobileDevice) {
      return (
        <>
          <div className="flex-1 flex flex-col min-w-0 pb-16">
            <main className="bg-background flex-1 min-h-screen min-w-0">
              <div className={cn("min-w-0", contentPaddingClass)}>{children}</div>
            </main>
          </div>
          <MobileBottomNav />
        </>
      );
    }

    // Desktop layout with sidebar
    return (
      <SidebarProvider>
        <div className={layoutWrapperClass}>
          <div>
            <Navigation />
          </div>
          <div className="flex-1 flex flex-col min-w-0">
            <main className="bg-background sm:rounded-2xl lg:rounded-2xl flex-1 min-h-screen min-w-0">
              <div className={cn("min-w-0", contentPaddingClass)}>{children}</div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  };

  return (
    <FrozenRouter>
      <ThemeProvider 
        attribute="class" 
        defaultTheme="system"
        enableSystem
        storageKey="theme"
        disableTransitionOnChange
        enableColorScheme={false}
      >
        <PlayerProvider>
          <div className={isFullScreenPage ? "h-screen" : "min-h-screen flex flex-col"}>
            {renderContent()}
          </div>
          {/* 播放器控件始终在外层渲染，无论 mounted 状态如何，确保不会被重新挂载 */}
          {shouldShowPlayerControls && <GlobalPlayerControls />}
        </PlayerProvider>
      </ThemeProvider>
    </FrozenRouter>
  );
}
